import { CopyOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { ApolloQueryResult } from '@apollo/client';
import { App, Popconfirm, Table, TableProps, Tooltip } from 'antd';
import { useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import {
  useGraphqlApiHeaders,
  useRestApiHeaders,
} from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { mapPlotterModeToGraphSetType } from 'business/data-analysis/constants';
import {
  useGraphSet,
  useGraphSetDispatch,
} from 'business/data-analysis/pages/graph/hooks/graph-context/contexts';
import { GraphSetDispatchActions } from 'business/data-analysis/pages/graph/hooks/graph-context/types';
import { useGraphSetDataDispatch } from 'business/data-analysis/pages/graph/hooks/graph-data-context/contexts';
import { GraphSetDataDispatchActions } from 'business/data-analysis/pages/graph/hooks/graph-data-context/types';
import { useMode } from 'business/data-analysis/pages/graph/hooks/use-mode';
import { sameGraphSet } from 'business/data-analysis/services/graph-set-comparison';
import { mapToGraphSetFromStructureDto } from 'business/data-analysis/services/map-to-graph-set-from-structure-dto';
import {
  useDataAnalysisControllerDuplicateGraphSet,
  useDataAnalysisControllerGetSavedGraphSet,
} from 'generated/apiComponents';
import {
  Exact,
  GetGraphSetListQuery,
  GetSavedGraphCountQuery,
  GraphSetTypeEnum_Enum,
  Module_Enum,
  useDeleteSavedGraphSetMutation,
  useGetGraphSetListQuery,
} from 'generated/graphql';
import Button from 'ui/button';
import Flex from 'ui/flex';

import { GraphSetTag } from './components/graph-set-tag';
import styles from './index.module.scss';

const useLoadGraphSet = (onSuccess: () => void) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { currentConstructionSiteId } = useAppContext();

  const dispatchGraphSet = useGraphSetDispatch();

  const [graphSetId, setGraphSetId] = useState('');

  const restHeaders = useRestApiHeaders(Module_Enum.DataAnalysis);

  const { data, error, isSuccess } = useDataAnalysisControllerGetSavedGraphSet(
    {
      pathParams: {
        constructionSiteId: currentConstructionSiteId ?? '',
        id: graphSetId,
      },
      headers: restHeaders,
    },
    {
      enabled: graphSetId.length !== 0,
      retry: false,
    },
  );

  if (graphSetId.length !== 0 && error) {
    message.warning(t('errors.error_generic'));
    setGraphSetId('');
  }

  if (graphSetId.length !== 0 && isSuccess && data) {
    const loadedStructure = mapToGraphSetFromStructureDto(data);
    dispatchGraphSet({
      type: GraphSetDispatchActions.LoadGraphSetStructure,
      structure: loadedStructure,
    });

    setGraphSetId('');

    onSuccess();
  }

  return [setGraphSetId];
};

interface GraphSetListProps {
  onClose: () => void;
  refetchGraphSetCount: (
    variables?:
      | Partial<
          Exact<{
            constructionSiteId: any;
            userId: any;
            type: GraphSetTypeEnum_Enum;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<GetSavedGraphCountQuery>>;
}

export const GraphSetList = ({
  onClose,
  refetchGraphSetCount,
}: GraphSetListProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { currentConstructionSiteId, user } = useAppContext();
  const headers = useGraphqlApiHeaders(Module_Enum.DataAnalysis);

  invariant(user && currentConstructionSiteId, 'No construction site id');

  const mode = useMode();
  const { structure, savedState } = useGraphSet();
  const dispatchGraphSet = useGraphSetDispatch();
  const dispatchGraphSetData = useGraphSetDataDispatch();

  const { data: graphSetList, refetch: graphSetListRefetch } =
    useGetGraphSetListQuery({
      fetchPolicy: 'network-only',
      variables: {
        constructionSiteId: currentConstructionSiteId,
        userId: user?.id,
        type: mapPlotterModeToGraphSetType[mode],
      },
      context: {
        headers,
      },
    });

  const [deleteSavedGraphSetMutation] = useDeleteSavedGraphSetMutation({
    context: {
      headers,
    },
  });

  const { mutate: duplicateGraphSet } =
    useDataAnalysisControllerDuplicateGraphSet();

  const [setGraphSetId] = useLoadGraphSet(onClose);

  const deleteGraphSet = async (id: string) => {
    await deleteSavedGraphSetMutation({
      variables: { id },
      onCompleted: async () => {
        await graphSetListRefetch();
        await refetchGraphSetCount();
        if (id === structure.id) {
          dispatchGraphSetData({
            type: GraphSetDataDispatchActions.UpdateValues,
            values: [],
          });
          dispatchGraphSet({
            type: GraphSetDispatchActions.ResetGraphSet,
            mode,
          });
        }
      },
      onError: () => {
        message.open({
          type: 'error',
          content: t('dataAnalysis.errors.graphSetDeletion'),
        });
      },
    });
  };

  const isSameGraphSet = sameGraphSet(savedState, structure);

  const onDuplicationClick = (graphSetId: string) => {
    duplicateGraphSet(
      {
        queryParams: {
          id: graphSetId,
        },
        pathParams: {
          constructionSiteId: currentConstructionSiteId,
        },
      },
      {
        onSuccess: () => {
          graphSetListRefetch();

          message.open({
            type: 'success',
            content: t('dataAnalysis.actions.duplication.message'),
          });
        },
        onError: (error) => {
          // @ts-expect-error Front generated conflict error type do not match backend type
          if (error.stack.statusCode === 409) {
            message.open({
              type: 'error',
              content: t('dataAnalysis.errors.duplicationAlreadyExists'),
            });
            return;
          }

          message.open({
            type: 'error',
            content: t('dataAnalysis.errors.duplicationGenericError'),
          });
        },
      },
    );
  };

  const columns: TableProps<
    GetGraphSetListQuery['graphSet'][number]
  >['columns'] = [
    {
      key: 'title',
      dataIndex: 'title',
    },
    {
      key: 'type',
      dataIndex: 'type',
      render: (graphSetType) => <GraphSetTag type={graphSetType} />,
    },
    {
      key: 'current',
      render: (_, record) =>
        structure.id === record.id ? (
          <Tooltip title={t('dataAnalysis.myGraphSets.currentGraphSet')}>
            <EyeOutlined />
          </Tooltip>
        ) : null,
    },
    {
      key: 'duplicate',
      align: 'right',
      render: (_, record) => (
        <Tooltip title={t('dataAnalysis.actions.duplication.duplicate')}>
          <Button
            icon={<CopyOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              onDuplicationClick(record.id);
            }}
            disabled={!savedState || !isSameGraphSet}
            type="text"
          />
        </Tooltip>
      ),
    },
    {
      key: 'delete',
      align: 'right',
      render: (_, record) => (
        <Popconfirm
          onConfirm={async (event) => {
            event?.stopPropagation();
            await deleteGraphSet(record.id);
          }}
          onCancel={(event) => event?.stopPropagation()}
          title={t('dataAnalysis.myGraphSets.deleteAction.title')}
          okText={t('dataAnalysis.myGraphSets.deleteAction.confirm')}
          cancelText={t('dataAnalysis.myGraphSets.deleteAction.cancel')}
          placement="topLeft"
        >
          <Button
            type="text"
            shape="circle"
            icon={<DeleteOutlined />}
            onClick={(event) => event.stopPropagation()}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Flex column className={styles.container}>
      <Table
        columns={columns}
        dataSource={graphSetList?.graphSet}
        showHeader={false}
        pagination={false}
        className={styles.list}
        rowKey={(record) => record.id}
        onRow={(record) => {
          return { onClick: () => setGraphSetId(record.id) };
        }}
      />

      <Flex justify={'flex-end'}>
        <Button type={'default'} size={'large'} onClick={onClose}>
          {t('common.close')}
        </Button>
      </Flex>
    </Flex>
  );
};
